/**
 * Abschnitt „So funktioniert's“: genau drei nummerierte Schritte (Req 4).
 *
 * Die Reihenfolge ist über Nummer (01/02/03) erkennbar, nicht nur über Farbe
 * (Req 4.4). Titel und Texte kommen aus dem i18n-Katalog.
 */

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import PedalBikeRoundedIcon from "@mui/icons-material/PedalBikeRounded";
import { useTheme } from "@mui/material/styles";
import type { ReactNode } from "react";
import { useI18n } from "../../i18n/I18nProvider";
import { welcomeColors } from "./welcomeTheme";
import { SectionLabel } from "./welcomeShared";

/** Ein Schritt des Ablaufs — Reihenfolge über `n`, Inhalt über i18n-Keys. */
interface Step {
  n: string;
  icon: ReactNode;
  titleKey: string;
  textKey: string;
}

const STEPS: Step[] = [
  {
    n: "01",
    icon: <Groups2RoundedIcon />,
    titleKey: "welcome.how.step1.title",
    textKey: "welcome.how.step1.text",
  },
  {
    n: "02",
    icon: <CalendarMonthRoundedIcon />,
    titleKey: "welcome.how.step2.title",
    textKey: "welcome.how.step2.text",
  },
  {
    n: "03",
    icon: <PedalBikeRoundedIcon />,
    titleKey: "welcome.how.step3.title",
    textKey: "welcome.how.step3.text",
  },
];

export default function HowItWorks() {
  const { t } = useI18n();
  const theme = useTheme();
  const colors = welcomeColors(theme.palette.mode);

  return (
    <Container maxWidth="lg">
      <Box id="so-gehts" sx={{ py: { xs: 8, md: 12 }, scrollMarginTop: 88 }}>
        <Box sx={{ maxWidth: 660, mx: "auto", textAlign: "center", mb: 6 }}>
          <SectionLabel>{t("welcome.how.sectionLabel")}</SectionLabel>
          <Typography variant="h2" component="h2" sx={{ fontSize: { xs: 30, md: 42 } }}>
            {t("welcome.how.sectionTitle")}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mt: 2, fontSize: 17, lineHeight: 1.7 }}
          >
            {t("welcome.how.sectionText")}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
            gap: 2.5,
          }}
        >
          {STEPS.map((step) => (
            <Card
              key={step.n}
              elevation={0}
              sx={{
                height: "100%",
                border: `1px solid ${colors.border}`,
                bgcolor: "background.paper",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 3 }}
                >
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 54,
                      height: 54,
                      bgcolor: colors.accentSoft,
                      color: "secondary.main",
                    }}
                  >
                    {step.icon}
                  </Avatar>
                  {/* Nummer statt nur Farbe (Req 4.4, 11.5) */}
                  <Typography
                    aria-hidden
                    sx={{ color: "text.secondary", fontWeight: 900, fontSize: 24 }}
                  >
                    {step.n}
                  </Typography>
                </Stack>
                <Typography variant="h3" component="h3" fontSize={21}>
                  {t(step.titleKey)}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ mt: 1.5, lineHeight: 1.75 }}
                >
                  {t(step.textKey)}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Container>
  );
}
